import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedStory() {
  const { shareId } = useParams();
  const [story, setStory] = useState(null);
  const panels = useMemo(
    () => [...(story?.panels || [])].sort((a, b) => a.index - b.index),
    [story]
  );
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/shared/stories/${shareId}`)
      .then(({ data }) => setStory(data))
      .catch(() => setMissing(true));
  }, [shareId]);

  if (missing) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="shared-story-missing">
        <p className="text-sm text-ink-soft">This story link is no longer available.</p>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24" data-testid="shared-story-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  const img = (id) => `${API}/shared/stories/${shareId}/image/${id}`;

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8" data-testid="shared-story-page">
      <Link to="/" data-testid="shared-story-logo">
        <img src="/logo.png" alt="ChatMiLu" className="h-10 w-auto" />
      </Link>

      <article className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-panel">
        {story.cover_creation_id && (
          <img
            data-testid="shared-story-cover"
            src={img(story.cover_creation_id)}
            alt={story.title}
            className="aspect-[16/9] w-full object-cover"
          />
        )}
        <div className="p-6 sm:p-8">
          <h1 dir="auto" data-testid="shared-story-title" className="font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">
            {story.title}
          </h1>

          {story.panels?.length > 0 && (
            <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="shared-story-panels">
              {panels.map((p) => (
                <img
                  key={p.creation_id}
                  src={img(p.creation_id)}
                  alt={`Panel ${p.index}`}
                  className="aspect-square w-full rounded-2xl border border-white/10 object-cover"
                />
              ))}
            </div>
          )}

          <div
            data-testid="shared-story-text"
            dir="auto"
            className="mt-8 whitespace-pre-wrap border-t border-white/10 pt-8 text-sm leading-loose text-ink-soft"
          >
            {story.text}
          </div>

          <Link
            to="/"
            data-testid="shared-story-cta"
            className="mt-10 inline-flex min-h-[44px] items-center rounded-full bg-brand px-6 text-sm text-white hover:bg-brand-hover"
          >
            <Sparkles className="me-2 h-4 w-4" /> Write your own with ChatMiLu
          </Link>
        </div>
      </article>
    </div>
  );
}
